import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { loadConfig } from '../api/data-qa-api';
import { ModelTypeType } from '../data-qa-constants';
import { ConfigSummary, DataQaDataset, MmmConfig } from '../data-qa-types';
import { groupConfigsByModel, ModelGroup, modelTypeForProductType } from '../data-qa-utils';

export interface ChannelExportModel {
  modelType: ModelTypeType;
  config: MmmConfig;
  dataset: DataQaDataset;
}

export interface ChannelExportData {
  // True while any of the company's models hasn't finished loading yet -- the
  // export button should stay disabled/loading until every model has settled,
  // since a partial export would silently omit whichever models weren't ready.
  loading: boolean;
  models: ChannelExportModel[];
  // Display labels for company models whose query settled with an ERROR (not
  // pending, so `loading` alone no longer covers them, and not success, so they
  // never make it into `models`) -- without surfacing these separately, the export
  // button would silently re-enable and produce an incomplete file with no
  // indication anything was missing (reviewer-caught on tinuiti-frontend#1703).
  failedModelLabels: string[];
}

interface GroupWithConfig {
  group: ModelGroup;
  config: ConfigSummary;
}

// Pulls every company model's full dataset for the "download all channels" Excel
// button -- deliberately NOT scoped to "other than the active model" (contrast
// useSectionDataInvariance), since the export covers every model in the company
// regardless of which model's Channels tab triggered it. Relies entirely on
// usePrefetchOtherModels having already warmed the cache for every model (it
// prefetches ALL discovered configs, not just fingerprint-identical ones) --
// same queryKey shape, so this observes those cache entries reactively rather
// than triggering new fetches once they've settled.
export const useChannelExportModels = (
  cid: string | undefined,
  configs: ConfigSummary[]
): ChannelExportData => {
  const groups = useMemo(() => groupConfigsByModel(configs), [configs]);

  // One representative config per model (Brand Equity's two stages carry
  // identical Channels data -- it's one of INVARIANCE_CHECKED_SECTIONS -- so each
  // group's primary/first config is enough). Paired with its group (rather than
  // two separately-filtered arrays) so `queries`/`results` below stay in
  // guaranteed lockstep with `groupsWithConfig`.
  const groupsWithConfig = useMemo(
    () =>
      groups
        .map(group => ({ group, config: group.configs[0] }))
        .filter((g): g is GroupWithConfig => Boolean(g.config)),
    [groups]
  );

  const queries = useMemo(
    () =>
      groupsWithConfig.map(({ config }) => ({
        queryKey: ['mmmDataQALoad', cid, config.configPath],
        queryFn: () => loadConfig(cid ?? '', config.configPath),
        staleTime: Infinity,
        enabled: Boolean(cid)
      })),
    [groupsWithConfig, cid]
  );

  const results = useQueries({ queries });

  return useMemo(() => {
    const loading = results.some(result => result.status === 'pending');
    const models: ChannelExportModel[] = [];
    const failedModelLabels: string[] = [];
    groupsWithConfig.forEach(({ group }, i) => {
      const result = results[i];
      const modelType = modelTypeForProductType(group.productType);
      if (result?.status === 'success') {
        models.push({ modelType, config: result.data.config, dataset: result.data.dataset });
      } else if (result?.status === 'error') {
        failedModelLabels.push(group.label);
      }
    });
    return { loading, models, failedModelLabels };
  }, [groupsWithConfig, results]);
};
